import React, { ReactNode, useEffect } from 'react'
import { EditionV4DTO } from '@west-australian-newspapers/publication-types'
import { format } from 'date-fns'
import { EditionItem } from './EditionItem'
import {
    InterlacedCta,
    StyledEditionList,
    StyledEditionsHeading,
    StyledEditionsPage,
    StyledMonthHeading,
    StyledGroupedEditions,
    StyledTravelEditionsSection,
} from './Editions.styled'
import {
    AllEvents,
    createEditionViewItemListEvent,
} from '@news-mono/web-common'
import { EditionsProps } from '../NightlyPreviousEditions'

interface GroupedEditionsProps {
    monthYear: string
    editions: EditionV4DTO[]
    lazyloadImages?: boolean
    onEvent: (event: AllEvents) => void
}

const GroupedEditions: React.FC<GroupedEditionsProps> = ({
    monthYear,
    editions,
    lazyloadImages,
    onEvent,
}) => {
    return (
        <StyledGroupedEditions>
            <StyledMonthHeading>{monthYear}</StyledMonthHeading>
            <StyledEditionList>
                {editions.map((edition, idx) => (
                    <EditionItem
                        edition={edition}
                        key={idx}
                        lazyloadImages={lazyloadImages}
                        onEvent={onEvent}
                    />
                ))}
            </StyledEditionList>
        </StyledGroupedEditions>
    )
}

export const TravelEditions: React.FC<EditionsProps> = ({
    editions,
    title,
    interlacedComponent,
    lazyloadImages,
    onEvent,
}) => {
    useEffect(() => {
        const event = createEditionViewItemListEvent({
            editions,
            collectionName: 'TheNightlyEditionPage',
            itemCategory: 'thenightly_editions_page_latest_editions',
        })
        event && onEvent(event)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const groupedEditions = groupEditionsByMonth(editions)

    return (
        <StyledEditionsPage>
            <StyledTravelEditionsSection>
                <StyledEditionsHeading marginBottom={40}>
                    {editions.length > 0 && <h2>{title}</h2>}
                </StyledEditionsHeading>
                {[...groupedEditions.entries()].map(
                    ([monthYear, editions], index) => (
                        <>
                            <GroupedEditions
                                monthYear={monthYear}
                                editions={editions}
                                lazyloadImages={lazyloadImages}
                                onEvent={onEvent}
                                key={index}
                            />

                            {index === 0 && interlacedComponent && (
                                <InterlacedCta withMarginBottom>
                                    {interlacedComponent}
                                </InterlacedCta>
                            )}
                        </>
                    ),
                )}
            </StyledTravelEditionsSection>
        </StyledEditionsPage>
    )
}

function groupEditionsByMonth(
    editions: EditionV4DTO[],
): Map<string, EditionV4DTO[]> {
    const editionsMap = new Map<string, EditionV4DTO[]>()
    editions.forEach((edition) => {
        const monthYear = format(new Date(edition.publicationDate), 'MMMM yyyy')
        if (!editionsMap.has(monthYear)) {
            editionsMap.set(monthYear, [])
        }
        editionsMap.get(monthYear)!.push(edition)
    })
    return editionsMap
}
